import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Printer } from "lucide-react";
import { toast } from "sonner";
import { http, idr, errMsg, fmtDate, todayISO, addDays } from "@/lib/api";
import Img from "@/components/Img";
import { useLang } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/useAutoTranslate";

const PH = ({ v, label }) => (v ? <span className="whitespace-pre-line">{v}</span> : <span className="text-amber-600 print:text-slate-400">[{label} — isi di Pengaturan → Umum]</span>);

export default function CommissionInvoice() {
  const { pid, month } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [sends, setSends] = useState([]);
  const [busy, setBusy] = useState(false);
  const { lang } = useLang();
  useAutoTranslate();
  const loadSends = () => http.get(`/extranet/properties/${pid}/commission-invoice/sends`, { params: { month } }).then((r) => setSends(r.data)).catch(() => {});
  useEffect(() => { if (localStorage.getItem("staff_token")) loadSends(); }, [pid, month]); // eslint-disable-line
  const sendMail = async () => {
    const to = d.p.notify_emails || [];
    if (!to.length) { toast.error("Isi email konfirmasi hotel di halaman Properti terlebih dahulu"); return; }
    if (!window.confirm(`${d.r.summary.commission === 0 ? "Total tagihan Rp 0. " : ""}Kirim invoice ke ${to.join(", ")}?`)) return;
    setBusy(true);
    try {
      const r = await http.post(`/extranet/properties/${pid}/commission-invoice/send`, { month, lang });
      toast.success(`Invoice terkirim ke ${r.data.to.join(", ")}${r.data.failed.length ? ` · gagal: ${r.data.failed.join(", ")}` : ""}`);
      loadSends();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  useEffect(() => {
    if (!localStorage.getItem("staff_token")) { nav("/extranet/login"); return; }
    Promise.all([http.get("/extranet/settings"), http.get(`/extranet/properties/${pid}`),
      http.get(`/extranet/properties/${pid}/commission-report`, { params: { month } })])
      .then(([s, p, r]) => setD({ s: s.data, p: p.data, r: r.data })).catch((e) => setError(errMsg(e)));
  }, [pid, month, nav]);

  if (error) return <p data-testid="invoice-error" className="p-10 text-center text-red-600">{error}</p>;
  if (!d) return <p className="p-10 text-center text-slate-400">Memuat invoice...</p>;
  const { s, p, r } = d;
  const issued = todayISO();
  const due = addDays(issued, Number(s.invoice_due_days ?? 14));
  const no = `INV/${month.replace("-", "")}/${(p.slug || p.id).toUpperCase().slice(0, 24)}`;
  const period = fmtDate(`${month}-01`, "MMMM yyyy");
  const T = r.summary;

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] justify-between px-4 print:hidden">
        <button data-testid="invoice-back" onClick={() => nav(-1)} className="flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold ring-1 ring-slate-200"><ArrowLeft className="h-4 w-4" /> Kembali</button>
        <div className="flex gap-2">
          <button data-testid="invoice-send-email" onClick={sendMail} disabled={busy} className="flex items-center gap-1.5 rounded-md border border-[#006CE4] bg-white px-4 py-2 text-sm font-semibold text-[#006CE4] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Kirim ke hotel</button>
          <button data-testid="invoice-print" onClick={() => window.print()} className="flex items-center gap-1.5 rounded-md bg-[#006CE4] px-4 py-2 text-sm font-semibold text-white"><Printer className="h-4 w-4" /> Cetak / Simpan PDF</button>
        </div>
      </div>
      <div className="mx-auto mb-4 max-w-[210mm] px-4 text-xs text-slate-600 print:hidden" data-testid="invoice-recipients">
        Penerima: {(p.notify_emails || []).length ? p.notify_emails.join(", ") : <span className="text-amber-600">belum ada — isi "Email konfirmasi ke hotel" di halaman Properti</span>}
        {sends.length > 0 && <span data-testid="invoice-last-sent"> · Terakhir dikirim {new Date(sends[0].at).toLocaleString("id-ID")} oleh {sends[0].by} ({sends.length}x)</span>}
      </div>
      <article data-testid="invoice-sheet" className="mx-auto max-w-[210mm] bg-white p-10 text-[13px] text-slate-800 shadow print:max-w-none print:p-0 print:shadow-none">
        <header className="flex items-start justify-between gap-6 border-b-2 border-[#1F5F8B] pb-5">
          <div>
            {s.logo ? <Img src={s.logo} size="sm" eager alt={s.invoice_company} className="mb-2 h-10 w-auto object-contain" /> : null}
            <p className="text-lg font-extrabold">{s.invoice_company || s.site_name}</p>
            <p className="mt-1 max-w-xs text-slate-600"><PH v={s.invoice_address} label="Alamat" /></p>
            <p className="text-slate-600">NPWP: <PH v={s.invoice_tax_id} label="NPWP" /></p>
            {s.contact_email && <p className="text-slate-600">{s.contact_email}{s.contact_phone ? ` · ${s.contact_phone}` : ""}</p>}
          </div>
          <div className="text-right">
            <p className="text-2xl font-extrabold tracking-tight text-[#1F5F8B]">INVOICE</p>
            <p className="text-slate-500">Komisi Booking Engine</p>
            <table className="mt-3 ml-auto text-right"><tbody>
              <tr><td className="pr-3 text-slate-500">No.</td><td data-testid="invoice-number" className="font-mono-x font-semibold">{no}</td></tr>
              <tr><td className="pr-3 text-slate-500">Tanggal</td><td>{fmtDate(issued, "d MMMM yyyy")}</td></tr>
              <tr><td className="pr-3 text-slate-500">Jatuh tempo</td><td data-testid="invoice-due">{fmtDate(due, "d MMMM yyyy")}</td></tr>
              <tr><td className="pr-3 text-slate-500">Periode</td><td data-testid="invoice-period">{period}</td></tr>
            </tbody></table>
          </div>
        </header>
        <section className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Ditagihkan kepada</p>
          <p data-testid="invoice-bill-to" className="mt-1 text-base font-bold">{p.name}</p>
          <p className="text-slate-600">{p.address || `${p.area}, ${p.city}`}</p>
          {(p.notify_emails || []).length > 0 && <p className="text-slate-600">{p.notify_emails.join(", ")}</p>}
        </section>
        <table className="mt-6 w-full" data-testid="invoice-lines">
          <thead className="border-y border-slate-300 bg-slate-50 text-left text-[11px] uppercase text-slate-500">
            <tr><th className="py-2 pl-2">Kode</th><th>Tamu</th><th>Check-in</th><th>Check-out</th><th className="text-right">Harga kamar</th><th className="text-right">%</th><th className="pr-2 text-right">Komisi</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {r.rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">Tidak ada booking pada periode ini</td></tr>}
            {r.rows.map((x) => (
              <tr key={x.code}><td className="py-1.5 pl-2 font-mono-x text-[11px]">{x.code}</td><td>{x.guest}</td><td>{fmtDate(x.check_in, "d MMM")}</td><td>{fmtDate(x.check_out, "d MMM")}</td>
                <td className="text-right">{idr(x.room_revenue)}</td><td className="text-right">{x.commission_percent}%</td><td className="pr-2 text-right">{idr(x.commission)}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <table className="w-72 text-right"><tbody>
            <tr><td className="py-1 text-slate-500">Jumlah booking</td><td>{T.bookings}</td></tr>
            <tr><td className="py-1 text-slate-500">Total harga kamar</td><td>{idr(T.room_revenue)}</td></tr>
            <tr className="border-t-2 border-[#1F5F8B] text-base font-extrabold"><td className="py-2">Total tagihan</td><td data-testid="invoice-total" className="text-[#1F5F8B]">{idr(T.commission)}</td></tr>
          </tbody></table>
        </div>
        <section className="mt-8 grid gap-4 border-t border-slate-200 pt-4 sm:grid-cols-2 print:grid-cols-2">
          <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400">Pembayaran ke</p><p className="mt-1"><PH v={s.invoice_bank} label="Rekening" /></p></div>
          <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400">Catatan</p>
            <p className="mt-1 text-slate-600">{(lang === "en" ? s.invoice_note_en : s.invoice_note) || s.invoice_note || `Komisi dihitung dari harga kamar untuk booking langsung terkonfirmasi dengan check-out di ${period}. Mohon cantumkan nomor invoice saat transfer.`}</p></div>
        </section>
      </article>
    </div>
  );
}
